import React, { useMemo, useState } from 'react'
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Menu,
} from '@mui/material'
import CodeDetailsAccordion from './CodeDetailsAccordion'
import { CodeValidationStatus } from '../../models/Validation'

export const GroupedTabContent = React.memo(
  ({ codeGroups, buildingData }: any) => {
    const [filter, setFilter] = useState<'Code Conflicts' | 'Design Conflicts'>(
      'Code Conflicts',
    )
    const [codeStatus, setCodeStatus] = useState<CodeValidationStatus | 'ALL'>(
      'ALL',
    )
    const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null)
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

    const GROUP_BATCH_SIZE = 20
    const [visibleGroupCount, setVisibleGroupCount] = useState(GROUP_BATCH_SIZE)

    const filteredGroups = useMemo(() => {
      let groups = codeGroups
        .map((g: any) => ({
          ...g,
          codes: g.codes.filter((c: any) => c.conflict === filter),
        }))
        .filter((g: any) => g.codes.length > 0)
      if (codeStatus && codeStatus !== 'ALL') {
        groups = groups
          .map((g: any) => ({
            ...g,
            codes: g.codes.filter((code: any) => {
              if (!code.elements || code.elements.length === 0) {
                return codeStatus === CodeValidationStatus.Failed
              }
              return code.elements.every((el: any) => el.status === codeStatus)
            }),
          }))
          .filter((g: any) => g.codes.length > 0)
      }
      return groups
    }, [codeGroups, filter, codeStatus])

    const sortedGroups = useMemo(() => {
      return [...filteredGroups].sort((a: any, b: any) => {
        if (sortOrder === 'asc') {
          return a.groupName.localeCompare(b.groupName)
        } else {
          return b.groupName.localeCompare(a.groupName)
        }
      })
    }, [filteredGroups, sortOrder])

    const visibleGroups = useMemo(() => {
      return sortedGroups.slice(0, visibleGroupCount)
    }, [sortedGroups, visibleGroupCount])

    const handleGroupScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setVisibleGroupCount((prev) =>
          Math.min(prev + GROUP_BATCH_SIZE, sortedGroups.length),
        )
      }
    }

    return (
      <>
        <Box
          display='flex'
          justifyContent='space-between'
          mb={2}
          alignItems='center'
          gap={'4px'}
        >
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, v) => v && setFilter(v)}
            size='small'
          >
            <ToggleButton
              value='Code Conflicts'
              sx={{
                bgcolor:
                  filter === 'Code Conflicts'
                    ? '#1705E5 !important'
                    : '#F5F5F5',
                color:
                  filter === 'Code Conflicts' ? '#fff !important' : 'inherit',
                borderRadius: 1,
                '&.Mui-selected': {
                  bgcolor: '#1705E5 !important',
                  color: '#fff !important',
                },
                '&.Mui-selected:hover': {
                  bgcolor: '#1705E5 !important',
                  color: '#fff !important',
                },
                fontSize: '11px',
              }}
            >
              Code Conflicts
            </ToggleButton>
            <ToggleButton
              value='Design Conflicts'
              sx={{
                bgcolor:
                  filter === 'Design Conflicts'
                    ? '#1705E5 !important'
                    : '#F5F5F5',
                color:
                  filter === 'Design Conflicts' ? '#fff !important' : 'inherit',
                borderRadius: 1,
                '&.Mui-selected': {
                  bgcolor: '#1705E5 !important',
                  color: '#fff !important',
                },
                '&.Mui-selected:hover': {
                  bgcolor: '#1705E5 !important',
                  color: '#fff !important',
                },
                fontSize: '11px',
              }}
            >
              Design Conflicts
            </ToggleButton>
          </ToggleButtonGroup>
          <FormControl size='small' sx={{ width: 100 }}>
            <Select
              value={codeStatus}
              onChange={(e) =>
                setCodeStatus(e.target.value as CodeValidationStatus | 'ALL')
              }
              displayEmpty
              sx={{ bgcolor: '#E4E4E4', height: '31px', fontSize: '11px' }}
            >
              <MenuItem value='ALL'>ALL</MenuItem>
              <MenuItem value={CodeValidationStatus.Passed}>
                {CodeValidationStatus.Passed}
              </MenuItem>
              <MenuItem value={CodeValidationStatus.Failed}>
                {CodeValidationStatus.Failed}
              </MenuItem>
            </Select>
          </FormControl>
          <Box ml='auto'>
            <IconButton
              aria-label='sort'
              onClick={(e) => setSortAnchorEl(e.currentTarget)}
              size='small'
              sx={{
                bgcolor: '#fff',
                border: '1px solid #E0E0E0',
                borderRadius: 1,
              }}
            >
              <svg
                width='20'
                height='15'
                viewBox='0 0 20 15'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M4 7.5H16M1 1.5H19M7 13.5H13'
                  stroke='#04002E'
                  strokeWidth='1.4'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </IconButton>
          </Box>
        </Box>
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={() => setSortAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { bgcolor: '#F5F5F5' } }}
        >
          <MenuItem
            selected={sortOrder === 'asc'}
            onClick={() => {
              setSortOrder('asc')
              setSortAnchorEl(null)
            }}
          >
            A-Z
          </MenuItem>
          <MenuItem
            selected={sortOrder === 'desc'}
            onClick={() => {
              setSortOrder('desc')
              setSortAnchorEl(null)
            }}
          >
            Z-A
          </MenuItem>
        </Menu>
        <Box
          sx={{ height: 'calc(100% - 60px)', overflowY: 'auto' }}
          onScroll={handleGroupScroll}
        >
          <CodeDetailsAccordion
            codeGroups={visibleGroups}
            buildingData={buildingData}
          />
        </Box>
      </>
    )
  },
)
